import * as _ from 'lodash';
import { Alert, Well } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../globals';
import FormGroup from '../../generic/Forms/FormGroup';
import LabeledPasswordInput from '../../generic/Forms/LabeledPasswordInput';
import LabeledTextInput from '../../generic/Forms/LabeledTextInput';
import PasswordInput from '../../generic/Forms/PasswordInput';
import Loader from '../../generic/Loader';
import Header from '../../shared/settings/SettingsPageHeader';
import Section from '../../shared/settings/SettingsPageSection';

import useUserProfile from './hooks/useUserProfile';

/**
 * Displays the login settings for the current user. This page allows them to change their
 * username, email, and password.
 */
const LoginSettings: VoidFunctionComponent = () => {
  const {
    userProfile,
    isSsoOnly,
    hasEnteredCurrentPassword,
    onUpdateUserProfile,
    onSave,
    isSaveButtonDisabled,
    isSaving,
  } = useUserProfile(window.user);

  if (!window.user) {
    return <Loader />;
  }

  const shouldDisableFormElements = isSsoOnly || !hasEnteredCurrentPassword;

  return (
    <div className="container margin-top-xl">
      <div className="panel padded">
        <Header heading="Account Settings">
          <Header.SaveButton
            onClick={onSave}
            isDisabled={isSaveButtonDisabled}
            isLoading={isSaving}
          />
        </Header>
        <Section>
          <Section.Left heading="Login settings">
            <Section.Paragraph>
              Login settings for FOSSA.
            </Section.Paragraph>
          </Section.Left>
          <Section.Right>
            {isSsoOnly ? (
              <Alert>This account is managed by your organization&apos;s SSO provider.</Alert>
            ) : (
              <Well className="margin-bottom-lg">
                <FormGroup>
                  <LabeledPasswordInput
                    isRequired
                    label="Enter your current password in order to save changes on this page."
                    value={userProfile.currentPassword}
                    placeholder="Current password"
                    onChange={(value) => {
                      onUpdateUserProfile({
                        currentPassword: value,
                      });
                    }}
                  />
                </FormGroup>
              </Well>
            )}
            <Section.Indented isDisabled={!hasEnteredCurrentPassword}>
              <FormGroup>
                <LabeledTextInput
                  labelVariant="legacy"
                  type="email"
                  label="Email"
                  value={userProfile.email}
                  placeholder="Email"
                  isDisabled
                  onChange={_.noop}
                />
              </FormGroup>
              <FormGroup>
                <LabeledTextInput
                  labelVariant="legacy"
                  type="text"
                  label="Username"
                  value={userProfile.username}
                  placeholder="Username"
                  isDisabled={shouldDisableFormElements}
                  onChange={(value) => {
                    onUpdateUserProfile({
                      username: value,
                    });
                  }}
                />
              </FormGroup>
              <FormGroup>
                <LabeledPasswordInput
                  label="Password"
                  value={userProfile.newPassword}
                  placeholder="Enter a new password"
                  onChange={(value) => {
                    onUpdateUserProfile({
                      newPassword: value,
                    });
                  }}
                  isDisabled={shouldDisableFormElements}
                />
                <PasswordInput
                  label="password-confirm" // This is just an a11y label
                  value={userProfile.newPasswordConfirm}
                  placeholder="Confirm new password"
                  onChange={(value) => {
                    onUpdateUserProfile({
                      newPasswordConfirm: value,
                    });
                  }}
                  isDisabled={shouldDisableFormElements}
                />
              </FormGroup>
            </Section.Indented>
          </Section.Right>
        </Section>
      </div>
    </div>
  );
};

export default LoginSettings;
